/**
 * v0.11.0 Button — single CTA pattern. Primary = the only amber filled
 * button in the app. Secondary = white with hover border. Tertiary =
 * link-style. Danger = red filled, used for destructive only.
 *
 *   <Button onClick={...}>Send</Button>
 *   <Button variant="secondary">Cancel</Button>
 *   <Button variant="primary" size="lg" loading>Saving...</Button>
 */
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  block?: boolean;       // full-width
  style?: CSSProperties;
  children?: ReactNode;
}

const VARIANT_STYLES: Record<ButtonVariant, CSSProperties> = {
  primary: {
    background: 'var(--xpb-primary)',
    color: '#fff',
    border: 'none',
  },
  secondary: {
    background: 'var(--xpb-surface)',
    color: 'var(--xpb-text-primary)',
    border: '0.5px solid var(--xpb-border-hover)',
  },
  tertiary: {
    background: 'transparent',
    color: 'var(--xpb-primary-hover)',
    border: 'none',
  },
  danger: {
    background: 'var(--xpb-danger)',
    color: '#fff',
    border: 'none',
  },
};

const SIZE_STYLES: Record<ButtonSize, CSSProperties> = {
  sm: { height: 32, padding: '0 12px', fontSize: 12 },
  md: { height: 40, padding: '0 16px', fontSize: 14 },
  lg: { height: 48, padding: '0 20px', fontSize: 16 },
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  block = false,
  disabled,
  style,
  children,
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;
  return (
    <button
      disabled={isDisabled}
      style={{
        ...VARIANT_STYLES[variant],
        ...SIZE_STYLES[size],
        borderRadius: 12,
        fontWeight: 500,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        transition: 'transform 0.1s ease, opacity 0.2s ease',
        opacity: isDisabled ? 0.55 : 1,
        width: block ? '100%' : undefined,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        whiteSpace: 'nowrap',
        ...style,
      }}
      onPointerDown={(e) => {
        if (!isDisabled) e.currentTarget.style.transform = 'scale(0.98)';
      }}
      onPointerUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      onPointerLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      {...rest}
    >
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      style={{
        width: 14, height: 14,
        border: '2px solid currentColor',
        borderRightColor: 'transparent',
        borderRadius: '50%',
        display: 'inline-block',
        animation: 'xpb-spin 0.7s linear infinite',
      }}
    />
  );
}
